/**
 * Search Suggestions Hook
 * 
 * Custom hook for search suggestions with debouncing:
 * - Debounced search queries
 * - Backend API integration
 * - Minimum character threshold
 * - Loading and error state management
 * 
 * @module hooks/useSearchSuggestions
 * @param {string} endpoint - API endpoint for suggestions
 * @param {number} [minChars=2] - Minimum characters before searching
 * @returns {Object} Search suggestions state and methods (query, suggestions, loading, error, setQuery)
 */
import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

export const useSearchSuggestions = (endpoint, minChars = 2) => {
  const { _t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search function that calls real API
  const fetchSuggestions = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < minChars) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${endpoint}/suggestions?q=${encodeURIComponent(searchQuery)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch suggestions');
        }

        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } catch (err) {
        setError(err.message);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [endpoint, minChars]
  );

  useEffect(() => {
    if (query) {
      fetchSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query, fetchSuggestions]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setQuery('');
  }, []);

  return {
    query,
    setQuery,
    suggestions,
    loading,
    error,
    clearSuggestions
  };
};

export default useSearchSuggestions;

